import type { ReactNode } from "react";
import { ComplianceStatusBadge } from "./compliance-status-badge";
import { VerbatimErrorText } from "./verbatim-error-text";

/**
 * Where a consignment stands with its e-way bill (`011` FR-005, FR-008, R6).
 *
 * Six states, and **expired and cancelled are both among them** (FR-008): neither is an error nor a
 * blank. A bill that was valid and no longer is describes a truck that may already have moved, and
 * rendering it as an empty field tells the Dispatcher nothing while looking exactly like "not
 * generated yet". Expiry and cancellation are distinct facts — one lapsed on its own, the other was
 * deliberately withdrawn (`cancelEWayBill`) — and must not share wording, the way `RegistrationState`
 * already keeps `cancelled` apart from `failed`/`expired` for the same reason.
 *
 * `not_applicable` is below the configured consignment-value threshold — nothing is missing (D-24).
 * `pending` covers "in flight" and "the service could not be reached", and is never rendered as
 * failed (FR-016, `009`'s precedent). `failed` means the portal answered and refused, with its
 * reason verbatim (FR-011).
 *
 * The number and the validity are the API's strings. No remaining-time countdown is computed here:
 * a skewed clock at a loading bay produces a confident wrong answer (FR-019, `002` R3).
 */
export type EWayBillStateName =
  | "not_applicable"
  | "pending"
  | "active"
  | "failed"
  | "expired"
  | "cancelled";

const SAYS: Record<EWayBillStateName, string> = {
  not_applicable:
    "This consignment is below the configured threshold for an e-way bill. Nothing is missing.",
  pending: "Generation is in progress, or the service could not be reached. Nothing has failed.",
  active: "This e-way bill is valid.",
  failed: "Generation was refused. The reason is below, exactly as it was returned.",
  expired: "This e-way bill has expired. It was valid; it is not any more.",
  cancelled: "This e-way bill was cancelled.",
};

export function EWayBillState({
  state,
  number,
  validUntil,
  reason,
  vehicle,
  children,
}: {
  state: EWayBillStateName;
  /** The e-way bill number, when there is one. No placeholder when there is not. */
  number?: string;
  /** The API's own wording for validity. Never a countdown computed here (FR-019). */
  validUntil?: ReactNode;
  /** The service's refusal, verbatim. Rendered only on `failed`. */
  reason?: string;
  /** The current vehicle, and the Part-B history when the caller has it (FR-006). */
  vehicle?: ReactNode;
  /** Update Part-B, or cancel with a reason (FR-007). Supplied by the app. */
  children?: ReactNode;
}) {
  return (
    <section
      data-slot="eway-bill-state"
      data-state={state}
      aria-label="E-way bill"
      className="border-muted bg-gray-0 flex flex-col gap-3 rounded-xl border p-4 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ComplianceStatusBadge
          subject="eway_bill"
          state={
            state === "active"
              ? "done"
              : state === "cancelled"
                ? "expired"
                : (state as "not_applicable" | "pending" | "failed" | "expired")
          }
        />
        {validUntil ? (
          <span className="text-muted-foreground text-xs">{validUntil}</span>
        ) : null}
      </div>

      <p className="text-muted-foreground">{SAYS[state]}</p>

      {number ? (
        <p className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            E-way bill number
          </span>
          <span className="font-mono text-xs break-all">{number}</span>
        </p>
      ) : null}

      {vehicle ? <div>{vehicle}</div> : null}

      {state === "failed" && reason ? (
        <VerbatimErrorText label="The e-way bill service's response">{reason}</VerbatimErrorText>
      ) : null}

      {children ? <div className="pt-0.5">{children}</div> : null}
    </section>
  );
}
